import { useQuery } from '@apollo/client/react';
import { Box, Paper, Typography, Skeleton, Stack } from '@mui/material';
import { ssrApolloService, Types, useRouteInfo } from '@scorenco/core';
import {
  NextAdArea,
  NextAkceloConfig,
  NextGooglePublisherTagConfig,
  NextPageWithLayout,
} from '@scorenco/next';

import { PageLayout, PageSeo } from '@/components';
import { WebLayout } from '@/layouts';
import { RouteKey } from '@/routes';
import { getSport } from '@/utils';

import { GetLeaguesBySportDocument } from './_internal/Competition.query.generated';
import { CompetitionList } from './_internal/CompetitionList';

const getAreaKey = (value: unknown) =>
  typeof value === 'string' && value.length > 0 ? value : 'unknown';

const Competitions: NextPageWithLayout<{ sport: Types.Clubs_Sport }> = ({
  sport,
}) => {
  const routeInfo = useRouteInfo();

  const sportSlug = routeInfo.query.sport as string;

  const leaguesBySportQuery = useQuery(GetLeaguesBySportDocument, {
    variables: { sportSlug },
    context: {
      role: 'anonymous',
    },
  });

  const sportName = sport?.name || sportSlug;

  const competitionByArea =
    leaguesBySportQuery.data?.competitions_league_detail_by_sport_slug?.reduce<
      Record<string, Types.Competitions_League_Detail[]>
    >((group, league) => {
      const areaKey = getAreaKey(league?.area);
      group[areaKey] = group[areaKey] ?? [];
      group[areaKey].push(league);
      return group;
    }, {}) || { unknown: undefined };

  return (
    <Box>
      <PageSeo
        title={`${[sportName, sport?.emoji]
          .filter(Boolean)
          .join('')} : retrouvez toutes les compétitions de ${sportName}`}
        description={`Découvrez la liste complète des compétitions françaises de ${sportName} sur Score'n'co . Suivez le calendrier, les résultats en direct et les classements de chaque compétition de ${sportName}.`}
        url={{
          routeKey: RouteKey.Competitions,
          params: {
            sport: sportSlug || '',
          },
        }}
      />

      <NextAkceloConfig />
      <NextGooglePublisherTagConfig />

      <PageLayout
        image={sport?.image}
        breadcrumbs={[
          {
            label: sport?.name || sportSlug,

            href: {
              routeKey: RouteKey.Sport,
              params: {
                sport: sportSlug,
              },
            },
          },
          {
            label: 'Ligues',
            current: true,
          },
        ]}
      >
        <Paper variant="bordered">
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={4}
            width="100%"
            alignItems="center"
          >
            <Typography variant="h1" component="h1">
              {sport ? (
                `${sport.emoji} ${sport.name}`
              ) : (
                <Skeleton width={150} />
              )}
            </Typography>
          </Stack>
        </Paper>

        <NextAdArea
          akceloSlotId="Scorenco_PlayRVideo"
          akceloAdUnitClass="akcelo-adunit-15786"
          withGooglePublisherTag
        />

        {competitionByArea &&
          Object.entries(competitionByArea).map(([area, leagues]) => {
            return (
              <CompetitionList
                key={area}
                sx={{ marginTop: 3 }}
                area={area}
                leagues={leagues}
              />
            );
          })}
      </PageLayout>
    </Box>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async ({ query }, previous) => {
    const { apolloClient, addApolloState } = ssrApolloService();

    const sportSlug = query.sport;

    if (typeof sportSlug !== 'string') {
      return { notFound: true };
    }

    if (query['no-ssr'] === undefined) {
      const sport = await getSport(sportSlug);
      if (!sport) {
        return { notFound: true };
      }

      // Chargement des données pour le rendu côté serveur
      await apolloClient.query({
        query: GetLeaguesBySportDocument,
        variables: { sportSlug },
      });

      return addApolloState({
        ...previous,
        props: {
          ...previous.props,
          sport,
        },
      });
    }

    return addApolloState(previous);
  },
]);

Competitions.getLayout = WebLayout.buildGetLayout({
  strapiMenu: true,
  amplify: { protectedPage: false },
});

export default Competitions;
